<script setup lang="ts">
    import { reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AccountStatus, type CurrentAccountReference } from '@shared/Root/Context.access';
    import { createChargebeePortalSession } from '@cwd/Account/CreditCards/CreditCards.access';
    import { chargebeeInstance } from '@shared/modules/chargebee';

    // Chargebee.js, loaded by chargebeeInstance.
    declare const Chargebee: any;

    const props = defineProps<{
        site: string;
        currentAccount: CurrentAccountReference | null;
    }>();

    const state = reactive({ busy: false });

    function showError(): void {
        bsAlert({ title: $Resources.ErrorHeading, content: $Resources.ErrorContactSupport, type: AlertTypes.Danger });
    }

    // Chargebee's card section (portal) as an overlay: add, remove and set the default card.
    async function manageCards(): Promise<void> {
        state.busy = true;
        try {
            const cbInstance = await chargebeeInstance(props.site);
            cbInstance.setPortalSession(() => createChargebeePortalSession());
            cbInstance.createChargebeePortal().openSection(
                { sectionType: Chargebee.getPortalSections().PAYMENT_SOURCES },
                {
                    close: () => {
                        // A new session next time: each click asks the server for one.
                        cbInstance.logout();
                        state.busy = false;
                    },
                    error: () => {
                        state.busy = false;
                        showError();
                    },
                }
            );
        } catch {
            state.busy = false;
            showError();
        }
    }
</script>

<template>
    <BsAlert
        v-if="props.currentAccount?.Status !== AccountStatus.Active"
        :type="AlertTypes.Warning"
        :dismissible="false"
        :title="$Resources.CreditCardActivationRequiredHeader"
        :content="$Resources.CreditCardActivationRequiredBody"></BsAlert>

    <template v-else>
        <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.ChargebeeCreditCardsIntroduction"></BsAlert>
        <div class="form-group">
            <button type="button" class="btn btn-primary" @click="manageCards" :disabled="state.busy">
                {{ $Resources.ChargebeeCreditCardsManageBtn }}
            </button>
        </div>
    </template>
</template>
